// @flow

import _ from 'lodash';
import ko from 'rgs-knockout';

import server from 'server2';

import ObservableMap from 'observable-map';
import { ObservableBusy } from 'observable-busy';

import { allOfficeRegions as allOfficeRegionsDictionary} from 'rgs-dictionary';

import type {
	Observable,
	ObservableArray,
	PureComputed,
} from 'rgs-knockout';


export type OfficeSelectComponentParams = {
	regionsMap: ObservableMap,
	settlementsMap: ObservableMap,
	officesMap: ObservableMap,
	regionId: Observable<number>,
	settlementId: Observable<string>,
	officeId: Observable<number>,
	busy: ObservableBusy,
	category: string,
}

export default class OfficeSelectViewModel {
	_params: OfficeSelectComponentParams
	_innerBusy: ObservableBusy
	_regionsOptions: ObservableArray
	_settlementsOptions: ObservableArray
	_officesOptions: ObservableArray
	_divisionName: PureComputed
	_regionId: Observable<number>
	_settlementId: Observable<string>
	_officeId: Observable<number>

	constructor (params: OfficeSelectComponentParams) {
		params = _.defaultsDeep({}, params, {
			regionsMap: undefined,
			settlementsMap: undefined,
			officesMap: undefined,
			regionId: ko.observable(),
			settlementId: ko.observable(),
			officeId: ko.observable(),
			busy: undefined,
			category: undefined,
		});

		this._params = params;

		this._innerBusy = new ObservableBusy();

		this._regionsOptions = ko.observableArray([]);
		this._settlementsOptions = ko.observableArray([]);
		this._officesOptions = ko.observableArray([]);

		this._getRegions();

		this._params.regionId.subscribe(v => {
			if (!v) {
				this._settlementsOptions([]);
				return;
			}
			this._getSettlementsByRegionId(v);
		});

		this._params.settlementId.subscribe(v => {
			if (!v) {
				this._officesOptions([]);
				return;
			}
			this._getOfficesBySettlementId(v);
		});

		this._divisionName = ko.pureComputed(() => {
			const office = this._params.officeId()
				&& _.find(this._officesOptions(), o => o.id === this._params.officeId());
			return office && office.name;
		});
	}

	_getRegions () {
		const loadData = data => {
			this._regionsOptions(data);
			this._addRecordsToDictionary(this._params.regionsMap, data);
		};

		const getData = async () => {
			const { responseJSON: { Data } } = await server.any.api.get(
				[
					'offices',
					'getAllRegions',
				],
				{
					query: {
						officeCategory: this._params.category,
					},
				}
			);
			loadData(Data);
		};

		const promise = this._params.category
				? getData()
				: allOfficeRegionsDictionary.ready()
					.then(data => loadData(data));

		this._innerBusy.add(promise);
		if (this._params.busy) {
			this._params.busy.add(promise);
		}
	}

	_getSettlementsByRegionId (regionId: number) {
		const getData = async () => {
			const { responseJSON: { Data } } = await server.any.api.get(
				[
					'offices',
					'getAllCities',
				],
				{
					query: {
						regionId,
						officeCategory: this._params.category,
					},
				}
			);
			this._settlementsOptions(Data);
			this._addRecordsToDictionary(this._params.settlementsMap, Data);
		};

		const promise = getData();

		this._innerBusy.add(promise);
		if (this._params.busy) {
			this._params.busy.add(promise);
		}
	}
	
	_getOfficesBySettlementId (settlementId: string) {
		const getData = async () => {
			const { responseJSON: { offices: { Data: { offices } } } } = await server.any.api.get(
				[
					'offices',
					'getOffices',
				],
				{
					query: {
						region: _.find(this._regionsOptions(),
							r => r.id === this._params.regionId()).name,
						city: _.find(this._settlementsOptions(),
							s => s.id === settlementId).name,
						district: '',
						street: '',
						metro: '',
						lat: '',
						lng: '',
						officeCategory: this._params.category,
					},
				}
			);
			this._officesOptions(offices);
			this._addRecordsToDictionary(this._params.officesMap, offices);
		};

		const promise = getData();

		this._innerBusy.add(promise);
		if (this._params.busy) {
			this._params.busy.add(promise);
		}
	}

	_addRecordsToDictionary (dictionary: ObservableMap, data: Array<Object>) {
		if (!dictionary) {
			return;
		}

		_.each(data, record => {
			if (dictionary.has(record.id)) {
				return;
			}
			dictionary.setAbsent(record.id, record, true);
		});

		dictionary.notify();
	}
}
